import React, {
  useMemo,
  useCallback,
  useContext,
  useState,
  useEffect,
} from 'react';
import { PromiseState } from 'react-refetch';
import { useDispatch, useSelector } from 'react-redux';
import delve from 'dlv';
import first from 'lodash.first';
import debounce from 'lodash.debounce';

import GenericComponent, {
  IToolbarItemType,
} from 'components/utility/GenericComponent/GenericComponent';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import SalesTypeDropdown from 'components/utility/SalesTypeDropdown';
import SearchField from 'components/styled/SearchField/SearchField';
import MobileSortingControls from 'components/data-visualization/Table/Sort';
import Pagination from 'components/data-visualization/Table/Pagination';
import TableControls from 'components/base/TableControls/TableControls';
import Table from './Table/Table';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import NoResults from './NoResults/NoResults';

import { logToAmplitude } from 'helpers/logAmplitudeEvent';
import getInitialSalesType from 'helpers/getInitialSalesType';
import getMobileSortingOptions from './shared/getMobileSortingOptions';
import { EntityContext } from 'context/entityContext';
import getTerritoryGridUrl from 'url-constructors/getTerritoryGridUrl';
import metrics, { IMetric } from 'maps/metrics';
import useMedia from 'hooks/useMedia';
import apiConnect from 'helpers/apiConnect';
import salesTypes from 'maps/sales-types';

import getSchema from 'schemas/componentSettings/territoryTable';

import useExportURL from 'hooks/useExportURL';
import useComponentSettings from 'hooks/useComponentSettings';
import { loadCountries } from 'redux/reducers/countries';
import useCustomerFilters from 'hooks/useCustomerFilters';
import { saveToApi, loadRecentHeaders } from 'redux/reducers/user/browseTable';

import { RootState } from 'redux/rootReducer';
import { SearchFieldBlock } from 'components/data-containers/ShortFormVideoTerritories/shared/shared.style';
import { SalesTypeDropdownWrapper } from 'components/data-containers/TerritoryTable/shared/TerritoryTable.style';
import { TTableHeader } from 'components/CustomizeView/CustomizeView';
import { TAsyncDispatchResult } from 'types/shared';
import usePrevious from 'hooks/usePrevious';
import { MAX_LIMIT } from 'config';

const ROW_LIMIT = 10;

function refetch(): any {
  return {
    fetchTerrritoryData: (params: any): any => ({
      territoryFetch: getTerritoryGridUrl(params),
    }),
  };
}

interface IOuterProps {
  isExternalUser: boolean;
  setSelectedSalesType?: (selectedSalesType?: any) => any;
  isStreamingPage?: boolean;
  topTerritoriesRef?: any;
  title?: string;
}

interface ITerritoryTableProps extends IOuterProps {
  fetchTerrritoryData: (params: any) => any;
  territoryFetch: PromiseState<any>;
}

/**
 * Territory Table shows the performance of an entity per country.
 *
 */
const TerritoryTable: React.FC<ITerritoryTableProps> = (props) => {
  const {
    fetchTerrritoryData,
    isExternalUser,
    territoryFetch = PromiseState.create(),
    setSelectedSalesType,
    topTerritoriesRef,
    isStreamingPage,
    title = 'Territory Table',
  } = props;
  const { entityType, entityIds, countryIds, filters, customRegionFilter } =
    useContext(EntityContext);

  const [territories, setTerritories] = useState<null | []>(null);
  const [territoryCount, setTerritoryCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [sortMetric, setSortMetric] = useState(metrics.twtd_only);
  const [sortOrder, setSortOrder] = useState('desc');
  const [rowOffset, setRowOffset] = useState(0);
  const [isDropdownShown, setShowDropdown] = useState(false);
  const prevFilter = usePrevious(filters);

  const withExpandableRow = entityType === 'artist' || entityType === 'project';
  const expandColumn = { label: 'expand', value: 'expand' };
  const streamingHeaders = [
    metrics.twtdOfGlobal,
    metrics.twtd_only,
    metrics.lwtd,
    metrics.twtd_change,
    metrics.lw_only,
    metrics.llw,
    metrics.lfw_change,
    metrics.rtd,
  ];
  const territoryPageHeaders = [metrics.twtd, metrics.lw, metrics.rtd];
  const tableHeaders = [
    { label: 'Position', value: 'countryRank' },
    { label: 'Territory', value: 'countryName' },
    ...(isStreamingPage ? [...streamingHeaders] : [...territoryPageHeaders]),
    ...(withExpandableRow ? [expandColumn] : []),
  ];
  const values = first<any>(delve(territoryFetch, 'value'));
  const { fulfilled, pending, rejected, reason } = territoryFetch;
  const initialSalesType = getInitialSalesType(isExternalUser, entityType);

  const countries = useSelector((state: RootState) => state.countries.data);
  const customersFulfilled = useSelector(
    (state: RootState) => state.customers.fulfilled
  );
  const isLargeScreen = useMedia(
    isStreamingPage ? '(min-width: 75rem)' : '(min-width: 50em)'
  );

  const territorySettingsSchema = getSchema(initialSalesType);
  const [componentSettings, saveComponentSettings] = useComponentSettings<any>(
    `territory-table-${entityType}`,
    territorySettingsSchema
  );
  const { selectedSalesType } = componentSettings;
  const [allowedCustomerFilters, customerFilter] =
    useCustomerFilters(selectedSalesType);
  const mobileSortingOptions = useMemo(getMobileSortingOptions, []);

  // Load country metadata on mount
  const dispatch = useDispatch();
  const debouncedSetSearchValue = useCallback(
    debounce(setSearchValue, 200),
    []
  );
  const tableType = 'streamingTerritories';

  useEffect(() => {
    if (JSON.stringify(prevFilter) !== JSON.stringify(filters)) {
      setRowOffset(0);
    }
  }, [filters])

  useEffect(() => {
    dispatch(loadCountries());
  }, [dispatch]);

  useEffect(() => {
    if (setSelectedSalesType) {
      setSelectedSalesType(selectedSalesType);
    }
  }, [selectedSalesType, setSelectedSalesType]);

  /**
   * Calls `fetchTerrritoryData` from props which is a lazy loaded
   * fetch function from `react-refetch`
   */
  useEffect(() => {
    if (customersFulfilled) {
      fetchTerrritoryData({
        entityIds,
        entityType,
        countryIds: countryIds?.length ? countryIds : filters?.country,
        salesType: selectedSalesType,
        customerFilter,
        sortOrder,
        sortMetric: sortMetric.value,
        nameFilter: searchValue,
        mediaTypes: filters?.media,
        region: filters?.region,
        customRegion: customRegionFilter,
        limit: ROW_LIMIT,
        offset: rowOffset,
      });
    }
  }, [
    rowOffset,
    selectedSalesType,
    searchValue,
    sortMetric,
    sortOrder,
    filters,
    customersFulfilled,
    customerFilter,
    fetchTerrritoryData,
    entityIds,
    entityType,
    countryIds,
    customRegionFilter,
  ]);

  /**
   * Merges performance with meta data and updates the territories state.
   * TerritoryTable required both meta data (e.g for flags) and perfomance data
   * for its rows. This function matches and merges both data sets together.
   */
  useEffect(() => {
    if (fulfilled && values) {
      const performance = values.entityData.countries;

      const merged = performance.map((country: any) => {
        const match = countries.find((meta) => meta.id === country.countryId);
        return Object.assign(country, match);
      });

      setTerritoryCount(values.entityData.totalCountryCount);
      setTerritories([...(rowOffset !== 0 && territories? territories : []), ...merged]);
    }
  }, [fulfilled, countries, values]);

  /** Get the latest table headers for BrowseOpus table */
  useEffect(() => {
    dispatch(loadRecentHeaders(tableType));
  }, [dispatch]);

  const exportUrl = useExportURL(territoryFetch, { offset: 0, limit: MAX_LIMIT });

  function setSalesType(salesType: any): void {
    if ('value' in salesType) {
      saveComponentSettings({ selectedSalesType: salesType.value });
      setRowOffset(0);
    }
  }

  /**
   * Callback from the seach input component
   */
  function handleSearch(event: any): void {
    const value = event.target.value;
    // inputValue quickly updates the value of the input
    setInputValue(value);
    setTerritories(null);
    setRowOffset(0);
    // searchValue gets send to the API and is debounced
    debouncedSetSearchValue(value);
  }

  function switchSortOrder(): void {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  }

  function changeSortMetric(metric: IMetric): void {
    setRowOffset(0);

    if (sortMetric.value === metric.value) {
      return switchSortOrder();
    }

    setSortMetric(metric);
    setSortOrder('desc');
  }

  const toggleDropdown = (): void => {
    setShowDropdown(!isDropdownShown);
    if (!isDropdownShown) {
      logToAmplitude('Custom View Button Clicked', { type: title });
    }
  };
  const saveTableHeaders = (headers: TTableHeader[]): TAsyncDispatchResult =>
    dispatch(saveToApi(headers, tableType));

  const formatHeaders = (header: TTableHeader): TTableHeader => {
    if (header.value === 'performance_wow_in_progress')
      header.label = 'Change (WTD)';
    if (header.value === 'performance_wow_last_completed')
      header.label = 'Change (LFW)';
    return header;
  };

  const savedHeaders: TTableHeader[] = useSelector(
    (state: RootState): any => (state.browseTable as any)[tableType]?.data || []
  );

  const hasDefaultHeaders = useMemo(
    () =>
      savedHeaders &&
      savedHeaders?.length > 0 &&
      tableHeaders.every((defaultItem: any) =>
        [...savedHeaders, { label: 'Territory', value: 'countryName' }].find(
          (header: any) => header.value === defaultItem.value
        )
      ),
    [tableHeaders, savedHeaders]
  );
  const customizeButtonText = useMemo(
    () => (hasDefaultHeaders ? 'Customize View' : 'Table has been customized'),
    [savedHeaders]
  );
  const headers = useMemo(() => {
    const positionHeader = savedHeaders.find(
      (header: any) => header.value === 'countryRank'
    );
    return isStreamingPage
      ? savedHeaders && savedHeaders.length > 0
        ? [
            ...(positionHeader ? [positionHeader] : []),
            { label: 'Territory', value: 'countryName' },
            ...(positionHeader
              ? savedHeaders.filter(
                  (header: any) => header.value !== 'countryRank'
                )
              : savedHeaders),
          ]
        : tableHeaders
      : tableHeaders;
  }, [isStreamingPage, savedHeaders, tableHeaders]);

  const availableHeadersWithoutTerritory = useMemo(() => {
    return isStreamingPage
      ? tableHeaders.filter((header: any) => header.value !== 'countryName')
      : tableHeaders;
  }, [isStreamingPage, tableHeaders]);

  const onShowMore = useCallback(() => {
    setRowOffset(rowOffset + ROW_LIMIT)
  }, [setRowOffset, rowOffset])

  return (
    <GenericComponent
      title={title}
      glossarySlug="charts-graphs-tables/territory-table"
      loading={pending}
      success={fulfilled}
      error={rejected}
      allowedFilters={[
        'country',
        ...allowedCustomerFilters,
        'media',
        'region',
        'customRegion',
      ]}
      extraToolbarItems={[
        {
          href: exportUrl,
          label: 'Export data',
          type: IToolbarItemType.Export,
          order: 1,
        },
      ]}
      isNarrowScreen
      isSmallScreen={!isLargeScreen}
      customizedMode={
        isStreamingPage
          ? {
              isShownCustomizeButton: true,
              onClick: toggleDropdown,
              customizeButtonText,
              isCustomizedActive: !hasDefaultHeaders,
              saveToApi: saveTableHeaders,
              formatItems: formatHeaders,
              savedItems: savedHeaders,
              allAvailableItems: availableHeadersWithoutTerritory,
              canTurnOffAll: false,
              isCustomizedDropdownShown: isStreamingPage && isDropdownShown,
            }
          : undefined
      }
      showToolbarDropdown={false}
      showToolbarOptions={true}
    >
      <ErrorBoundary error={reason} errorInfo={reason ? reason.cause : null}>
        <TableControls ref={topTerritoriesRef} alignCenteredVertically>
          <SearchFieldBlock alignCenteredVertically={isLargeScreen}>
            <SearchField
              handleSearch={handleSearch}
              value={inputValue}
              placeholder="Search"
            />
          </SearchFieldBlock>
          <SalesTypeDropdownWrapper>
            <SalesTypeDropdown
              entityType={entityType}
              onChange={setSalesType}
              selectedItem={salesTypes[selectedSalesType]}
              hasMedia={!!filters?.media}
              hasSplitDownloads
              isRoundedDropdown="all"
            />
          </SalesTypeDropdownWrapper>
          <MobileSortingControls
            hide={isLargeScreen}
            sortOrder={sortOrder}
            handleToggle={switchSortOrder}
            handleDropdown={changeSortMetric}
            dropdownItems={mobileSortingOptions}
            activeItem={sortMetric}
            isRoundedDropdown="all"
          />
        </TableControls>
        {(territories as any)?.length === 0 ? (
          <NoResults
            entityType={entityType}
            searchValue={searchValue}
            isLargeScreen={isLargeScreen}
            selectedSalesType={selectedSalesType}
          />
        ) : (
          <AnimatedLoadingWrapper loading={pending}>
            <Table
              territories={territories}
              tableHeaders={headers}
              sortOrder={sortOrder}
              sortMetric={sortMetric}
              setSortOrder={setSortOrder}
              setSortMetric={setSortMetric}
              changeSortMetric={changeSortMetric}
              rowLimit={ROW_LIMIT + rowOffset}
              isLargeScreen={isLargeScreen}
              selectedSalesType={selectedSalesType}
              withExpandableRows={withExpandableRow}
              isStreamingPage={isStreamingPage}
              /** One column is always Territory. So minimum number of headers is 2 */
              hasOneHeader={headers?.length === 2}
            />
            {territories && searchValue.length === 0 && (
              <Pagination
                current={territories.length ?? 0}
                total={territoryCount}
                offset={territoryCount}
                type="territories"
                hasMoreResults={ROW_LIMIT + rowOffset < territoryCount}
                onClick={onShowMore}
                customShowMoreText={'Show more'}
                isWideButtonWithTotal
              />
            )}
          </AnimatedLoadingWrapper>
        )}
      </ErrorBoundary>
    </GenericComponent>
  );
};

export default apiConnect(refetch)(
  TerritoryTable as any
) as unknown as React.FC<IOuterProps>;
